import { type DictTypes } from '@/constants';
import { type CacheConfig } from '@/types/api';

import {
  cacheData,
  cacheDict,
  getCachedData,
  getCachedDict
} from './cacheData';

export const cachedApi = async <K>({
  path,
  call,
  cacheConfig
}: {
  path: string;
  call: () => Promise<K>;
  cacheConfig: CacheConfig & { shortTerm: boolean };
}): Promise<K> => {
  const cachedData = getCachedData(path, {
    shortTerm: cacheConfig.shortTerm
  });

  const request = async () => {
    const response = await call();
    cacheData(path, response, { shortTerm: cacheConfig.shortTerm });
    return response as K;
  };

  if (!cachedData || cacheConfig.ignoreCache) return request();
  return cachedData as K;
};

export const cachedDictApi = async <T extends DictTypes, K>({
  dictType,
  call,
  cacheConfig
}: {
  dictType: T;
  call: () => Promise<K>;
  cacheConfig: CacheConfig;
}): Promise<K> => {
  const cachedData = getCachedDict(dictType);

  const request = async () => {
    const response = await call();
    cacheDict(dictType, response);
    return response as K;
  };

  if (!cachedData || cacheConfig.ignoreCache) return request();
  return cachedData as K;
};

class AbortControllerManager {
  private activeControllers = new Set<AbortController>();

  createController(): AbortController {
    const controller = new AbortController();
    this.activeControllers.add(controller);
    return controller;
  }

  removeController(controller: AbortController): void {
    this.activeControllers.delete(controller);
  }

  abortAll(): void {
    this.activeControllers.forEach((controller) => {
      controller.abort();
    });
    this.activeControllers.clear();
  }

  getActiveCount(): number {
    return this.activeControllers.size;
  }
}

export const abortControllerManager = new AbortControllerManager();
